import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router';
import { Form, Card, Row, Col, Input, Select, Checkbox, Divider, Spin, DatePicker, Button, Space } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { fetchData, saveData } from '../../service/dataServices';
import moment from 'moment';
import CustomAddNewPageContent from '../../../components/Content/CustomAddNewPageContent';
import { getFormItems } from '../../components/form/formComponents';
import { getOperationType } from '../../components/common-constants';
import UserDropdown from '../../components/form/userDropdown';
import { useTranslation } from 'react-i18next';
import MainImageUploader from '../../components/image-upload/MainImageUploader';
import { getLocalizedTitle } from '../../utils/localization';
import ImageListUploader from '../../components/image-upload/ImageListUploader';
import DocumentUploader from '../../components/image-upload/DocumentUploader';
import GlbUploader from '../../components/image-upload/GlbUploader';
import UserContext from '../../../pages/layout/UserContext';
import checkFormTypePermission from '../../../components/Authorization/FormTypePermission';
import ForbiddenResult from '../../../components/Authorization/ForbiddenResult';
import FormChangeHistory from './FormChangeHistory';
import GenericFormPdf from '../../../components/Pdf/GenericFormPdf';
import TranslatableInput from '../../components/form/TranslatableInput';

const { Option } = Select;

export default function CustomFormAdd() {
  const { t, i18n } = useTranslation();
  const { formId, entityId } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const currentUser = useContext(UserContext);

  const hasReadPerm = checkFormTypePermission(currentUser, formId, 'read');
  const hasWritePerm = checkFormTypePermission(currentUser, formId, 'write');

  const operation = getOperationType(entityId);
  const [schema, setSchema] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [allDropdowns, setAllDropdowns] = useState({})
  const [dropdownIdMap, setDropdownIdMap] = useState({});

  const [objects, setObjects] = useState([]);
  const [objectFilters, setObjectFilters] = useState({});
  const [recordId, setRecordId] = useState('');
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [users, setUsers] = useState([]);
  const [exportButtonClicked, setExportButtonClicked] = useState(false);

  // Watch the objectId field for changes
  const objectIdValue = Form.useWatch('objectId', form);
  useEffect(() => {
    if (objectIdValue) {
      setSelectedObjectId(objectIdValue);
    }
  }, [objectIdValue]);

  // State for image uploads: keyed by field id
  const [imageFileLists, setImageFileLists] = useState({});

  const getImageFileList = (fieldId) => imageFileLists[fieldId] || [];
  const setImageFileList = useCallback((fieldId) => (listOrUpdater) => {
    setImageFileLists(prev => {
      const currentList = prev[fieldId] || [];
      const newList = typeof listOrUpdater === 'function' ? listOrUpdater(currentList) : listOrUpdater;
      return { ...prev, [fieldId]: newList };
    });
  }, []);

  fetchData('/api/dropdowns', setAllDropdowns);
  fetchData('/api/dropdowns/types', (types) => {
    const idMap = {};
    types?.map(t => {
      idMap[t.id] = t.title
    })
    setDropdownIdMap(idMap);
  });

  // 1. Fetch Schema
  fetchData(`/api/form-schema/${formId}`, (data) => {
    setSchema(data);
    if (data?.objectCategoryId) {
      setObjectFilters((prev) => ({ ...prev, category: data.objectCategoryId }))
    }
  });

  // 2. Fetch Initial Data (Update Case)
  fetchData(`/api/form-record/${entityId ?? ''}`, setInitialData, undefined, !!entityId);

  // 3. Fetch Objects for Dropdown
  const objectResponse = fetchData('/api/objects', (data) => setObjects(data?.content || []), objectFilters);

  // 4. Fetch next record ID (Create mode only)
  fetchData(`/api/form-record/next-record-id/${formId}`, (data) => {
    setRecordId(data);
  }, undefined, !entityId && !!formId);

  // 5. Fetch users for resolving user_dropdown field values in the PDF export
  fetchData('/api/users/user-refs', setUsers);

  // 5. Mutation Logic
  const saveMutation = saveData(`/api/form-record`, operation?.method);
  const { getFormSubmitButton } = getFormItems(null, true);

  // --- Effects ---

  // Populate form and set selected object ID when initialData is loaded
  useEffect(() => {
    if (initialData && schema) {
      const data = initialData.content || initialData;
      const newImageFileLists = {};

      // Set recordId from existing data in update mode
      if (initialData.recordId) {
        setRecordId(initialData.recordId);
      }

      // Convert date strings to moment objects for DatePicker, and populate image file lists
      // Use field.id as the content key - field names can change but IDs remain stable.
      schema?.sections?.forEach(section => {
        section.rows?.forEach(row => {
          row.cols?.forEach(col => {
            const fieldKey = col.field?.id;
            if ((col.field?.type === 'text' || col.field?.type === 'long_text') && data[fieldKey] && typeof data[fieldKey] === 'string') {
              data[fieldKey] = { en: data[fieldKey], ar: '' };
            }
            if (col.field?.type === 'date' && data[fieldKey]) {
              data[fieldKey] = moment(data[fieldKey]);
            }
            if (col.field?.type === 'single_photo' && data[fieldKey]) {
              newImageFileLists[fieldKey] = Array.isArray(data[fieldKey]) ? data[fieldKey] : [data[fieldKey]];
              delete data[fieldKey];
            }
            if (col.field?.type === 'multiple_photos' && data[fieldKey]) {
              newImageFileLists[fieldKey] = Array.isArray(data[fieldKey]) ? data[fieldKey] : [];
              delete data[fieldKey];
            }
            if (col.field?.type === 'pdf_upload' && data[fieldKey]) {
              newImageFileLists[fieldKey] = Array.isArray(data[fieldKey]) ? data[fieldKey] : [];
              delete data[fieldKey];
            }
            if (col.field?.type === 'model_3d' && data[fieldKey]) {
              newImageFileLists[fieldKey] = Array.isArray(data[fieldKey]) ? data[fieldKey] : [data[fieldKey]];
              delete data[fieldKey];
            }
          });
        });
      });

      if (Object.keys(newImageFileLists).length > 0) {
        setImageFileLists(newImageFileLists);
      }

      form.setFieldsValue(data);

      // If the record already has an objectId, set it in the form
      if (initialData.objectId) {
        form.setFieldsValue({ objectId: initialData.objectId });
      }
    }
  }, [initialData, form, schema]);

  // --- Handlers ---

  const onSearch = (val) => {
    setObjectFilters((prev) => ({ ...prev, inventoryNumber: val }));
  };

  const onFinish = (values) => {
    // Convert moment date objects to ISO strings for the API
    // Use field.id as the content key - field names can change but IDs remain stable.
    const processedValues = { ...values };
    schema?.sections?.forEach(section => {
      section?.rows?.forEach(row => {
        row?.cols?.forEach(col => {
          const fieldKey = col.field?.id;
          if (col.field?.type === 'date' && processedValues[fieldKey]) {
            processedValues[fieldKey] = processedValues[fieldKey].format('YYYY-MM-DD');
          }
        });
      });
    });

    // Add image data to the payload
    schema?.sections?.forEach(section => {
      section?.rows?.forEach(row => {
        row?.cols?.forEach(col => {
          const fieldKey = col.field?.id;
          if (col.field?.type === 'single_photo') {
            const fileList = imageFileLists[fieldKey];
            processedValues[fieldKey] = fileList && fileList.length > 0 ? fileList[0] : null;
          }
          if (col.field?.type === 'multiple_photos') {
            processedValues[fieldKey] = imageFileLists[fieldKey] || [];
          }
          if (col.field?.type === 'pdf_upload') {
            processedValues[fieldKey] = imageFileLists[fieldKey] || [];
          }
          if (col.field?.type === 'model_3d') {
            const fileList = imageFileLists[fieldKey];
            processedValues[fieldKey] = fileList && fileList.length > 0 ? fileList[0] : null;
          }
        });
      });
    });

    const dataToSend = {
      formId: formId,
      recordId: recordId,
      ...processedValues,
    };

    const mutationPayload = entityId ? { dataToSend, id: entityId } : { dataToSend };

    saveMutation.mutate(mutationPayload, {
      onSuccess: () => {
        history.push(`/custom-form-show/${formId}`);
      }
    });
  };

  const getDropdownOptions = (field) => {
    const res = allDropdowns?.[dropdownIdMap?.[field.dropdownType]]
    if (!res) {
      return []
    }
    return res?.map(r => {
      return {
        value: r.id,
        label: getLocalizedTitle(r),
      }
    })
  }

  const resolveFieldValue = (field, rawValue) => {
    switch (field.type) {
      case 'boolean':
        return { type: 'text', value: rawValue ? t('common.yes', 'Yes') : t('common.no', 'No') };
      case 'dropdown': {
        const option = getDropdownOptions(field).find(o => o.value === rawValue);
        return { type: 'text', value: option ? option.label : '' };
      }
      case 'user_dropdown': {
        const user = users.find(u => u.id === rawValue);
        return { type: 'text', value: user ? user.name : '' };
      }
      case 'date':
        return { type: 'text', value: rawValue ? moment(rawValue).format('YYYY-MM-DD') : '' };
      case 'single_photo': {
        const list = imageFileLists[field.id] || [];
        return { type: 'image', value: list[0] ? list[0].url : null };
      }
      case 'multiple_photos': {
        const list = imageFileLists[field.id] || [];
        return { type: 'gallery', value: list.map(f => f.url).filter(Boolean) };
      }
      case 'pdf_upload': {
        const list = imageFileLists[field.id] || [];
        return { type: 'text', value: list.map(f => f.name || f.url).join(', ') };
      }
      case 'model_3d': {
        const list = imageFileLists[field.id] || [];
        return { type: 'text', value: list[0] ? list[0].name : '' };
      }
      case 'text':
      case 'long_text':
        return { type: 'text', value: rawValue?.[i18n.language] || rawValue?.en || '' };
      default:
        return { type: 'text', value: rawValue };
    }
  };

  const buildPdfReport = () => {
    const values = form.getFieldsValue();
    const selectedObject = objects.find(o => o.id === selectedObjectId);

    const sections = (schema?.sections || []).map(section => ({
      title: translated(section.title) || '',
      rows: (section.rows || []).map(row => ({
        columns: (row.cols || []).map(col => {
          const field = col.field;
          const resolved = field ? resolveFieldValue(field, values[field.id]) : { type: 'text', value: '' };
          return {
            weight: getColWidth(col.width) || 1,
            fields: [{ label: field ? translated(field.name) : '', ...resolved }],
          };
        }),
      })),
    }));

    return {
      title: translated(schema?.name) || 'Form',
      subtitle: [
        selectedObject ? `${t('customForm.selectObject')}: ${selectedObject.inventoryNumber}` : null,
        recordId ? `${t('customForm.recordId')}: ${recordId}` : null,
      ].filter(Boolean).join('   '),
      fileName: `${translated(schema?.name) || 'form'}_${recordId || 'draft'}_(${new Date().toISOString().slice(0, 10)}).pdf`,
      sections,
    };
  };

  const handleExport = () => setExportButtonClicked(true);
  const exportCallback = () => setExportButtonClicked(false);

  const renderField = (field) => {
    if (!field) return null;
    const rules = hasWritePerm ? [{ required: field.isRequired, message: t('customForm.isRequired', { field: translated(field.name) }) }] : [];

    switch (field.type) {
      case 'long_text':
        return <Form.Item name={field.id} label={translated(field.name)} rules={rules}>
          <TranslatableInput
              disabled={!hasWritePerm}
              textarea={true}
              placeholder={{ en: 'Enter long text', ar: 'أدخل نصاً طويلاً' }}
          /></Form.Item>;
      case 'number':
        return <Form.Item name={field.id} label={translated(field.name)} rules={rules}><Input type="number" disabled={!hasWritePerm} /></Form.Item>;
      case 'boolean':
        return <Form.Item name={field.id} valuePropName="checked" rules={rules}><Checkbox disabled={!hasWritePerm}>{translated(field.name)}</Checkbox></Form.Item>;
      case 'dropdown':
        return (
          <Form.Item name={field.id} label={translated(field.name)} rules={rules}>
            <Select placeholder={t('customForm.selectOptionPlaceholder')} options={getDropdownOptions(field)} allowClear disabled={!hasWritePerm} />
          </Form.Item>
        );
      case 'user_dropdown':
        return (
          <UserDropdown
            label={translated(field.name)}
            fieldName={field.id}
            hasWritePermission={hasWritePerm}
          />
        );
      case 'date':
        return (
          <Form.Item name={field.id} label={translated(field.name)} rules={rules}>
            <DatePicker style={{ width: '100%' }} disabled={!hasWritePerm} />
          </Form.Item>
        );
      case 'single_photo': {
        const fieldKey = field.id;
        return (
          <Form.Item label={translated(field.name)} rules={rules}>
            <MainImageUploader
              storageFolder="custom-forms"
              fileList={getImageFileList(fieldKey)}
              setFileList={setImageFileList(fieldKey)}
              disabledAdd={!hasWritePerm}
            />
          </Form.Item>
        );
      }
      case 'multiple_photos': {
        const fieldKey = field.id;
        return (
          <Form.Item label={translated(field.name)} rules={rules}>
            <ImageListUploader
              storageFolder="custom-forms"
              maxImageSize={20}
              fileList={getImageFileList(fieldKey)}
              setFileList={setImageFileList(fieldKey)}
              disabledAdd={!hasWritePerm}
            />
          </Form.Item>
        );
      }
      case 'pdf_upload': {
        const fieldKey = field.id;
        return (
          <Form.Item label={translated(field.name)} rules={rules}>
            <DocumentUploader
              storageFolder="custom-forms"
              maxImageSize={20}
              multiple={true}
              fileList={getImageFileList(fieldKey)}
              setFileList={setImageFileList(fieldKey)}
              disabledAdd={!hasWritePerm}
            />
          </Form.Item>
        );
      }
      case 'model_3d': {
        const fieldKey = field.id;
        return (
          <Form.Item label={translated(field.name)} rules={rules}>
            <GlbUploader
              storageFolder="custom-forms"
              fileList={getImageFileList(fieldKey)}
              setFileList={setImageFileList(fieldKey)}
              disabledAdd={!hasWritePerm}
            />
          </Form.Item>
        );
      }
      default:
        return <Form.Item name={field.id} label={translated(field.name)} rules={rules}>            
        <TranslatableInput
              disabled={!hasWritePerm}
              placeholder={{ en: 'Enter text', ar: 'أدخل النص' }}
        /></Form.Item>;
    }
  };

  if (!hasReadPerm && !hasWritePerm) {
    return <ForbiddenResult />;
  }

  if (!schema) return <Spin />;

  function translated(val) {
    return val?.[i18n.language] || val?.['en']
  }

  function getColWidth(width) {
    if (width === '1') {
      return 24
    } else if (width === '1/2') {
      return 12
    } else if (width === '1/3') {
      return 8
    } else if (width === '1/4') {
      return 6
    } else {
      return 24
    }
  }

  return (
    <CustomAddNewPageContent
      title={`${!hasWritePerm ? t('customForm.view', 'View') : t(`common.${operation?.type?.toLowerCase()}`, operation?.type || '')} ${translated(schema?.name) || ''}`}
      subTitle={t('customForm.subTitle')}
      exportButton={
        <Space>
          <Button type="primary" onClick={handleExport}>
            {t('customForm.exportPdf', 'Export PDF')}
          </Button>
          {selectedObjectId && (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => history.push(`/save-object/${selectedObjectId}`)}
            >
              {t('customForm.viewObject')}
            </Button>
          )}
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
      >
        {/* Object Selection and Record ID Section */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="objectId"
              label={t('customForm.selectObject')}
              rules={hasWritePerm ? [{ required: true, message: t('customForm.selectObjectRequired') }] : []}
            >
              {/* Once a form is submitted (entityId present), the object it was submitted
              for is bound to that record and shouldn't be changed - only new submissions
              can pick an object. */}
              <Select
                showSearch
                placeholder={t('customForm.selectObjectPlaceholder')}
                allowClear
                onSearch={onSearch}
                filterOption={false}
                loading={objectResponse?.isFetching}
                disabled={!hasWritePerm || !!entityId}
              >
                {objects.map((option) => (
                  <Option value={option.id} key={option.id}>
                    {option.inventoryNumber}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('customForm.recordId')}
            >
              <Input value={recordId} readOnly />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        {schema.sections?.map(section => (
          <Card key={section.id} title={translated(section?.title)} style={{ marginBottom: 20 }}>
            {section?.rows?.map(row => (
              <Row key={row.id} gutter={16}>
                {row?.cols?.map(col => (
                  <Col
                    key={col.id}
                    span={getColWidth(col.width)}
                  >
                    {renderField(col.field)}
                  </Col>
                ))}
              </Row>
            ))}
          </Card>
        ))}
        <Divider />
        {hasWritePerm && (
          <Row justify="end">
            {getFormSubmitButton(false)}
          </Row>
        )}
      </Form>
      <FormChangeHistory recordId={entityId} formId={formId} />
      {exportButtonClicked && (
        <GenericFormPdf {...buildPdfReport()} callback={exportCallback} />
      )}
    </CustomAddNewPageContent>
  );
}